/**
 * TypeScript wrapper for the WASM-compiled FlatBuffer parser.
 *
 * The Rust nom parser (fbs_parser_lib) is compiled to WASM via fbs_parser_wasm.
 * This module handles dynamic loading + initialization and provides typed
 * interfaces matching the Rust AST's serde output.
 */

// ---------------------------------------------------------------------------
// TypeScript interfaces matching Rust AST (serde Serialize output)
// ---------------------------------------------------------------------------

export type WasmFbsScalar =
  | "Bool"
  | "Byte"
  | "UByte"
  | "Short"
  | "UShort"
  | "Int"
  | "UInt"
  | "Long"
  | "ULong"
  | "Float"
  | "Double"
  | "Int8"
  | "UInt8"
  | "Int16"
  | "UInt16"
  | "Int32"
  | "UInt32"
  | "Int64"
  | "UInt64"
  | "Float32"
  | "Float64";

export type WasmFbsType =
  | { Scalar: WasmFbsScalar }
  | "String"
  | { Named: string }
  | { Vector: WasmFbsType }
  | { Array: [WasmFbsType, number] };

export interface WasmFbsMetadataEntry {
  key: string;
  value: string | null;
}

export interface WasmFbsVariant {
  name: string;
  line_number: number;
  comment: string | null;
  explicit_value: number | null;
}

export interface WasmFbsEnum {
  name: string;
  backing_type: WasmFbsType;
  variants: WasmFbsVariant[];
  metadata: WasmFbsMetadataEntry[];
  open_brace_line: number;
  close_brace_line: number;
}

export interface WasmFbsUnion {
  name: string;
  members: string[];
}

export interface WasmFbsField {
  name: string;
  field_type: WasmFbsType;
  default_value: string | null;
  metadata: WasmFbsMetadataEntry[];
}

export interface WasmFbsTable {
  name: string;
  fields: WasmFbsField[];
  metadata: WasmFbsMetadataEntry[];
}

export interface WasmFbsStruct {
  name: string;
  fields: WasmFbsField[];
  metadata: WasmFbsMetadataEntry[];
}

export type WasmSchemaItem =
  | { Include: string }
  | { Namespace: string }
  | { Attribute: string }
  | { Table: WasmFbsTable }
  | { Struct: WasmFbsStruct }
  | { Enum: WasmFbsEnum }
  | { Union: WasmFbsUnion }
  | { RootType: string }
  | { FileIdentifier: string }
  | { FileExtension: string };

export interface WasmFbsFile {
  path: string;
  namespace: string | null;
  enums: WasmFbsEnum[];
  unions: WasmFbsUnion[];
  tables: WasmFbsTable[];
  structs: WasmFbsStruct[];
  includes: string[];
  items: WasmSchemaItem[];
  lines: string[];
}

// ---------------------------------------------------------------------------
// WASM module management
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let wasmModule: any = null;
let initPromise: Promise<void> | null = null;

/**
 * Dynamically imports and initializes the WASM module.
 * Safe to call multiple times — subsequent calls return the cached promise.
 */
export async function initWasm(): Promise<void> {
  if (wasmModule) return undefined;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // Dynamic import of the wasm-pack generated JS glue
      // (src/wasm/ is gitignored; built locally via `npm run build:wasm`)
      // eslint-disable-next-line import/no-unresolved, import/extensions
      const wasm = await import("../../../wasm/fbs_parser/fbs_parser_wasm");
      await wasm.default();
      wasmModule = wasm;
    } catch (err) {
      initPromise = null;
      throw err;
    }
  })();

  return initPromise;
}

/**
 * Returns true if the WASM module has been initialized.
 */
export function isWasmReady(): boolean {
  return wasmModule !== null;
}

/**
 * Parse a .fbs file content string using the WASM parser.
 * Throws if WASM is not initialized or if parsing fails.
 */
export function parseFbsWasm(content: string, pathLabel: string): WasmFbsFile {
  if (!wasmModule) {
    throw new Error("WASM parser not initialized. Call initWasm() first.");
  }

  const jsonResult: string = wasmModule.parse_fbs(content, pathLabel);
  const parsed = JSON.parse(jsonResult);

  if (parsed.error) {
    throw new Error(`WASM parse error: ${parsed.error}`);
  }

  return parsed as WasmFbsFile;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a WasmFbsType to a human-readable string */
export function formatFbsType(t: WasmFbsType): string {
  if (t === "String") return "string";
  if (typeof t === "object") {
    if ("Scalar" in t) return t.Scalar.toLowerCase();
    if ("Named" in t) return t.Named;
    if ("Vector" in t) return `[${formatFbsType(t.Vector)}]`;
    if ("Array" in t) return `[${formatFbsType(t.Array[0])}:${t.Array[1]}]`;
  }
  return String(t);
}
